import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Film, ImagePlus, Sparkles, Video, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { btnGhost, btnPrimary, Field, inputCls, PageHead, Panel } from "./ui";
import { MAX_VIDEO, VideoInput } from "./editor/VideoManager";

const NewVideos = ({ videos, setVideos, disabled }) => {
  const add = (f) => {
    if (!f) return;
    if (f.size > MAX_VIDEO) return toast.error("Video troppo grande (max 100 MB): registra un video più breve");
    setVideos([...videos, f]);
  };
  return (
    <div className="mt-5 pt-4 border-t border-[#D5B98C]/50" data-testid="new-product-videos">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
        <h4 className="text-xs uppercase tracking-widest text-[#39251D]">Video (facoltativo)</h4>
        <div className="flex gap-4">
          <VideoInput testId="new-product-video-gallery" label="Scegli video" icon={Film} onFile={add} disabled={disabled} />
          <VideoInput testId="new-product-video-camera" label="Registra" icon={Video} capture onFile={add} disabled={disabled} />
        </div>
      </div>
      {videos.map((v, i) => (
        <div key={i} data-testid={`new-product-video-${i}`} className="flex items-center justify-between gap-2 py-1.5 text-sm">
          <span className="truncate"><Film className="inline w-4 h-4 mr-1.5 text-[#956842]" />{v.name || "video"} · {(v.size / 1048576).toFixed(1)} MB</span>
          <button type="button" data-testid={`new-product-remove-video-${i}`} onClick={() => setVideos(videos.filter((_, k) => k !== i))} className="w-7 h-7 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
      ))}
      <p className="mt-1 text-[11px] text-[#242321]/55">MP4, MOV o WEBM, max 100 MB. Il video va nella galleria dopo le foto; l'AI non lo elabora.</p>
    </div>
  );
};

export default function AdminNewProduct() {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState(1);
  const [busy, setBusy] = useState(false);
  const [videos, setVideos] = useState([]);
  const [status, setStatus] = useState("");
  const previews = files.map((f) => URL.createObjectURL(f));
  useEffect(() => () => previews.forEach(URL.revokeObjectURL), [files]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e, withAi = true) => {
    e?.preventDefault();
    if (withAi && !files.length) return toast.error("Carica almeno una fotografia");
    if (price === "") return toast.error("Inserisci il prezzo: non viene mai assegnato dall'AI");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products", { price: Number(price), stock: Number(stock) });
      for (const [i, v] of videos.entries()) {
        const fd = new FormData();
        fd.append("file", v);
        await api.post(`/admin/products/${data.id}/videos`, fd, { onUploadProgress: (ev) => ev.total && setStatus(`Video ${i + 1}/${videos.length}: ${Math.round((ev.loaded / ev.total) * 100)}%`) });
      }
      setStatus("");
      if (files.length) {
        const fd = new FormData();
        files.forEach((f) => fd.append("files", f));
        await api.post(`/admin/products/${data.id}/photos`, fd, { params: { analyze: withAi } });
      }
      toast.success(withAi ? "Bozza creata: scheda AI in compilazione" : "Bozza creata: compila la scheda a mano");
      navigate(`/admin/prodotti/${data.id}`);
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid="admin-new-product" className="max-w-3xl">
      <PageHead title="Nuovo prodotto" subtitle="Foto → scheda AI (bozza + rifinitura) → revisione → approvazione → pubblicazione. Le foto restano come le carichi; nulla viene pubblicato senza la tua approvazione." />
      <Panel title="Fotografie" className="mb-5">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {previews.map((src, i) => (
            <div key={src} className="relative aspect-[4/5] bg-[#E9DFCE]">
              <img src={src} alt="" className="w-full h-full object-cover" />
              <button type="button" data-testid={`new-product-remove-photo-${i}`} onClick={() => setFiles(files.filter((_, k) => k !== i))} className="absolute top-1 right-1 w-7 h-7 bg-[#F5F0E8]/90 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="aspect-[4/5] stitch flex flex-col items-stretch justify-center gap-2 p-2 text-xs text-[#956842]">
            <label data-testid="new-product-gallery-button" className="flex flex-col items-center gap-1.5 py-3 cursor-pointer hover:bg-[#D5B98C]/15 transition-colors text-center">
              <ImagePlus className="w-6 h-6" strokeWidth={1.4} /> Scegli dalla galleria
              <input data-testid="new-product-photos-input" type="file" accept="image/*" multiple className="hidden"
                onChange={(e) => { setFiles([...files, ...Array.from(e.target.files)]); e.target.value = ""; }} />
            </label>
            <label data-testid="new-product-camera-button" className="flex flex-col items-center gap-1.5 py-3 border-t border-[#D5B98C]/50 cursor-pointer hover:bg-[#D5B98C]/15 transition-colors text-center">
              <Camera className="w-6 h-6" strokeWidth={1.4} /> Scatta foto
              <input data-testid="new-product-camera-input" type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { setFiles([...files, ...Array.from(e.target.files)]); e.target.value = ""; }} />
            </label>
          </div>
        </div>
        <p className="mt-3 text-xs text-[#242321]/55">Puoi selezionare più foto insieme. JPG, PNG o WEBP, max 15 MB. Gli originali vengono sempre conservati.</p>
        <NewVideos videos={videos} setVideos={setVideos} disabled={busy} />
      </Panel>
      <Panel title="Prezzo e quantità" className="mb-6">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Prezzo (€)" hint="Lo inserisci tu: l'AI non assegna mai prezzi"><input data-testid="new-product-price" type="number" min="0" step="0.01" required value={price} onChange={(e) => setPrice(e.target.value)} className={inputCls} /></Field>
          <Field label="Quantità" hint="1 = pezzo unico / seconda mano"><input data-testid="new-product-stock" type="number" min="0" required value={stock} onChange={(e) => setStock(e.target.value)} className={inputCls} /></Field>
        </div>
      </Panel>
      <div className="flex flex-col sm:flex-row gap-3">
        <button data-testid="new-product-submit" disabled={busy} className={`${btnPrimary} py-4 text-[13px]`}><Sparkles className="w-4 h-4" /> {busy ? (status || "Caricamento…") : "Carica foto e crea scheda con AI"}</button>
        <button type="button" data-testid="new-product-manual" disabled={busy} onClick={() => submit(null, false)} className={`${btnGhost} py-4`}>Crea a mano, senza AI</button>
      </div>
    </form>
  );
}
